import type { GameContract, VersionedSave } from '@vpgc/core';

export const PLANET_RUNNER_SCHEMA_VERSION = 1;
export const PLANET_RUNNER_NO_INPUT: Input = { mask: 0, pressedMask: 0 };

export const INPUT_LEFT = 1 << 0;
export const INPUT_RIGHT = 1 << 1;
export const INPUT_JUMP = 1 << 2;
export const INPUT_ZOOM_IN = 1 << 3;
export const INPUT_ZOOM_OUT = 1 << 4;
export const INPUT_TOGGLE_CAMERA = 1 << 5;

export type CameraMode = 'locked' | 'deadzone';
export type PlayerMotionState = 'idle' | 'idle-long' | 'walk' | 'run' | 'jump' | 'fall' | 'land' | 'swim' | 'blocked';
export type StatusMessageKind = 'none' | 'left-action' | 'minimap-unlocked' | 'water' | 'returned-home' | 'forest-blocked';

export interface GameState {
  schemaVersion: typeof PLANET_RUNNER_SCHEMA_VERSION;
  seed: number;
  rng: number;
  tick: number;
  cameraZoom: number;
  cameraMode: CameraMode;
  cameraAngle: number;
  minimapUnlocked: boolean;
  statusMessage: StatusMessageKind;
  statusTicks: number;
  swimStamina: number;
  inWater: boolean;
  homeReturnTicks: number;
  playerSurfaceDistance: number;
  player: {
    angle: number;
    altitude: number;
    tangentVelocity: number;
    radialVelocity: number;
    facing: -1 | 1;
    grounded: boolean;
    coyoteTicks: number;
    jumpBufferTicks: number;
    walkCycle: number;
    landingTicks: number;
    idleTicks: number;
    blockedTicks: number;
  };
  discoveries: {
    leftActionSeen: boolean;
    rightActionSeen: boolean;
  };
}

export interface Input {
  mask: number;
  pressedMask: number;
}

export interface ViewModel {
  tick: number;
  camera: {
    x: number;
    y: number;
    zoom: number;
    rotation: number;
    mode: CameraMode;
    deadzoneHalfWidthPx: number;
  };
  planet: {
    x: number;
    y: number;
    radius: number;
    atmosphere: number;
    surfaceStroke: number;
  };
  player: {
    x: number;
    y: number;
    angle: number;
    altitude: number;
    tangentX: number;
    tangentY: number;
    upX: number;
    upY: number;
    facing: -1 | 1;
    tangentVelocity: number;
    radialVelocity: number;
    grounded: boolean;
    inWater: boolean;
    motionState: PlayerMotionState;
    walkCycle: number;
    landing01: number;
  };
  features: Array<{
    id: string;
    kind: 'action' | 'water' | 'forest';
    label: string;
    distance: number;
    endDistance?: number;
    x: number;
    y: number;
    endX?: number;
    endY?: number;
    active: boolean;
    unlocked: boolean;
  }>;
  discoveries: {
    minimapUnlocked: boolean;
    leftActionSeen: boolean;
    rightActionSeen: boolean;
  };
  world: {
    w: number;
    h: number;
  };
  diagnostics: {
    speed: number;
    altitude: number;
    zoom: number;
    surfaceDistance: number;
    swimStamina: number;
  };
  status: {
    kind: StatusMessageKind;
    ticks: number;
  };
}

export interface SaveJson extends VersionedSave {
  schemaVersion: typeof PLANET_RUNNER_SCHEMA_VERSION;
  seed: number;
  rng: number;
  tick: number;
  cameraZoom: number;
  cameraMode: CameraMode;
  cameraAngle: number;
  minimapUnlocked: boolean;
  statusMessage: StatusMessageKind;
  statusTicks: number;
  swimStamina: number;
  inWater: boolean;
  homeReturnTicks: number;
  playerSurfaceDistance: number;
  player: {
    angle: number;
    altitude: number;
    tangentVelocity: number;
    radialVelocity: number;
    facing: -1 | 1;
    grounded: boolean;
    coyoteTicks: number;
    jumpBufferTicks: number;
    walkCycle: number;
    landingTicks: number;
    idleTicks: number;
    blockedTicks: number;
  };
  discoveries: {
    leftActionSeen: boolean;
    rightActionSeen: boolean;
  };
}

export type PlanetRunnerContract = GameContract<GameState, Input, ViewModel, SaveJson>;
